import { IFRAME_QUERY_PARAMS, THEME_QUERY_PARAMS } from '../../protocol';
import type { FieldRoute, ToIframeMessage } from '../../protocol';

const MIN_IFRAME_HEIGHT = 200;
const MAX_IFRAME_HEIGHT = 500;

export interface IframeTheme {
  accent?: string;
  accentHover?: string;
  accentSoft?: string;
  fontFamily?: string;
  textColor?: string;
  borderColor?: string;
  borderRadius?: string | number;
  /** Declares a FIXED iframe height (Mercado-Pago-like model), skipping the
   * dynamic measure-and-clamp flow entirely. When omitted, height stays
   * driven by `src/inputs`' `content-resize` measurement clamped via
   * `clampHeight`. */
  height?: string | number;
  /** Declares a fixed iframe width, used in place of the default
   * `width:100%`. */
  width?: string | number;
  /** Passed through as a query param only — `src/inputs` owns its own
   * CSS application. */
  padding?: string;
}

/** Per-field `style`-by-state allowlist (Engram odd/sdk-options/tasks, item
 * 5). Closed CSS property set — `src/inputs/adapters/config.ts` validates
 * every value against a strict regex before applying anything; an unknown
 * key or an invalid value is dropped silently, both here (nothing gets
 * serialized) and there (defense in depth). */
export interface FieldStyle {
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
  fontStyle?: string;
  letterSpacing?: string;
  lineHeight?: string;
  textAlign?: string;
  textTransform?: string;
}

export interface FieldStyleByState {
  base?: FieldStyle;
  focus?: FieldStyle;
  invalid?: FieldStyle;
  complete?: FieldStyle;
  /** Only `color`/`fontStyle`/`fontWeight` are meaningful for `::placeholder`. */
  placeholder?: Pick<FieldStyle, 'color' | 'fontStyle' | 'fontWeight'>;
}

export interface BuildIframeSrcOptions {
  /** Which entry point to load — one of the 3 field routes or the headless
   * `coordinator` route (design D1: dedicated 4th headless iframe). */
  route: FieldRoute;
  token: string;
  /** The merchant's site key (S4 rename, formerly the `pk_`-prefixed
   * publishable key), carried on every iframe `src` so the coordinator can
   * authorize the parent origin against it. Optional at this
   * transport-level type only so pre-existing theme-only call sites stay
   * valid; every real caller (`FieldGroup`/`CardFieldSession`) always
   * passes it. */
  siteKey?: string;
  /** Shared `groupId` nonce (design D2) — every iframe in the same field
   * group carries the same value so they open the same `BroadcastChannel`. */
  group: string;
  nonce: string | number;
  theme?: IframeTheme;
  locale?: string;
  /** Per-field placeholder/accessible-name override (Engram
   * odd/sdk-options/tasks, item 2). Passed through as query params only —
   * `src/inputs` owns validation/application. */
  placeholder?: string;
  ariaLabel?: string;
  /** Per-field `style`-by-state defaults/overrides (Engram
   * odd/sdk-options/tasks, item 5). Serialized as one compact JSON query
   * param (`style`); `src/inputs` owns validation and CSS-custom-property
   * application. */
  style?: FieldStyleByState;
}

export interface CreateIframeElementOptions {
  /** D4 — the coordinator iframe carries no UI and must not be observable
   * as a sizable surface: `position:absolute;width:0;height:0;border:0;
   * visibility:hidden` (not `display:none`, which can defer script
   * execution in some engines). */
  headless?: boolean;
  /** A consumer-declared fixed height (from `theme.height`). When present,
   * this is applied as the iframe's initial (and permanent) `style.height`
   * instead of `MIN_IFRAME_HEIGHT`, and the caller is expected to ignore
   * later `content-resize` measurements. */
  height?: string | number;
  /** A consumer-declared fixed width (from `theme.width`), used in place of
   * the default `width:100%`. */
  width?: string | number;
}

/** A bare number is interpreted as px (matching the existing `border_radius`
 * convention); a string is passed through as a literal CSS size. */
function toCssSize(value: string | number): string {
  return typeof value === 'number' ? `${value}px` : value;
}

/** The closed set `src/inputs`'s `getLocale()` understands (mirrors
 * `src/inputs/ui/i18n.ts`'s `Locale`). Kept as a literal duplicate rather
 * than an import — `src/sdk` may only import `src/protocol`, never
 * `src/inputs` (bundle boundary guard). */
const SUPPORTED_LANGUAGE_SUBTAGS = new Set(['en', 'es', 'pt']);

/** Normalizes a BCP 47 locale tag (`es-AR`, `pt-BR`, `en-US`, `EN`, ...) to
 * the bare, lowercase language subtag `src/inputs` actually understands
 * (Engram odd/sdk-options/tasks, item 1). An unsupported or malformed tag
 * falls back to `en` — same silent-fallback convention `src/inputs`'s own
 * `getLocale()` already uses. Exported for `session.ts`/tests. */
export function normalizeLocale(tag: string | undefined): string {
  if (!tag) return 'en';
  const subtag = tag.split('-')[0]?.toLowerCase();
  return subtag !== undefined && SUPPORTED_LANGUAGE_SUBTAGS.has(subtag) ? subtag : 'en';
}

/** Builds the iframe `src`, translating the public `theme` object's
 * camelCase keys to `src/inputs`'s real snake_case query params
 * (`accent`, `accent_hover`, `accent_soft`, `font_family`, `text_color`,
 * `border_color`, `border_radius`). Every value is passed through
 * `encodeURIComponent` — no raw interpolation (threat matrix: `mount()`
 * target/token injection). */
export function buildIframeSrc(origin: string, options: BuildIframeSrcOptions): string {
  const { route, token, siteKey, group, nonce, theme, locale = 'en', placeholder, ariaLabel, style } = options;
  let src = `${origin}/${route}.html?${IFRAME_QUERY_PARAMS.token}=${encodeURIComponent(token)}`;
  if (siteKey !== undefined) {
    src += `&${IFRAME_QUERY_PARAMS.siteKey}=${encodeURIComponent(siteKey)}`;
  }
  src +=
    `&${IFRAME_QUERY_PARAMS.group}=${encodeURIComponent(group)}` +
    `&${IFRAME_QUERY_PARAMS.nonce}=${encodeURIComponent(String(nonce))}`;

  if (theme) {
    if (theme.accent !== undefined) {
      src += `&${THEME_QUERY_PARAMS.accent}=${encodeURIComponent(theme.accent)}`;
    }
    if (theme.accentHover !== undefined) {
      src += `&${THEME_QUERY_PARAMS.accentHover}=${encodeURIComponent(theme.accentHover)}`;
    }
    if (theme.accentSoft !== undefined) {
      src += `&${THEME_QUERY_PARAMS.accentSoft}=${encodeURIComponent(theme.accentSoft)}`;
    }
    if (theme.fontFamily !== undefined) {
      src += `&${THEME_QUERY_PARAMS.fontFamily}=${encodeURIComponent(theme.fontFamily)}`;
    }
    if (theme.textColor !== undefined) {
      src += `&${THEME_QUERY_PARAMS.textColor}=${encodeURIComponent(theme.textColor)}`;
    }
    if (theme.borderColor !== undefined) {
      src += `&${THEME_QUERY_PARAMS.borderColor}=${encodeURIComponent(theme.borderColor)}`;
    }
    if (theme.borderRadius !== undefined) {
      src += `&${THEME_QUERY_PARAMS.borderRadius}=${encodeURIComponent(String(theme.borderRadius))}`;
    }
    if (theme.height !== undefined) {
      src += `&${THEME_QUERY_PARAMS.height}=${encodeURIComponent(String(theme.height))}`;
    }
    if (theme.width !== undefined) {
      src += `&${THEME_QUERY_PARAMS.width}=${encodeURIComponent(String(theme.width))}`;
    }
    if (theme.padding !== undefined) {
      src += `&${THEME_QUERY_PARAMS.padding}=${encodeURIComponent(theme.padding)}`;
    }
  }

  src += `&${IFRAME_QUERY_PARAMS.locale}=${encodeURIComponent(normalizeLocale(locale))}`;
  if (placeholder !== undefined) {
    src += `&${IFRAME_QUERY_PARAMS.placeholder}=${encodeURIComponent(placeholder)}`;
  }
  if (ariaLabel !== undefined) {
    src += `&${IFRAME_QUERY_PARAMS.ariaLabel}=${encodeURIComponent(ariaLabel)}`;
  }
  if (style !== undefined) {
    src += `&${IFRAME_QUERY_PARAMS.style}=${encodeURIComponent(JSON.stringify(style))}`;
  }
  return src;
}

/** Builds the `<iframe>` element via `createElement`/`setAttribute` only —
 * never `innerHTML` (threat matrix: `mount()` target injection). */
export function createIframeElement(src: string, options?: CreateIframeElementOptions): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('src', src);
  iframe.setAttribute('title', 'Card payment fields');
  iframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-same-origin');
  const heightValue = options?.height !== undefined ? toCssSize(options.height) : `${MIN_IFRAME_HEIGHT}px`;
  const widthValue = options?.width !== undefined ? toCssSize(options.width) : '100%';
  iframe.setAttribute(
    'style',
    options?.headless
      ? 'position:absolute;width:0;height:0;border:0;visibility:hidden'
      : `height:${heightValue};width:${widthValue};border:0`,
  );
  return iframe;
}

/** Clamps an attacker/iframe-reported height to a fixed 200-500px range —
 * the concrete UI-redress mitigation (threat matrix: attacker-controlled
 * height). */
export function clampHeight(height: number): number {
  return Math.min(MAX_IFRAME_HEIGHT, Math.max(MIN_IFRAME_HEIGHT, height));
}

/** Applies a clamped height to the iframe element's inline style and
 * returns the clamped value actually applied. */
export function applyClampedHeight(iframe: HTMLIFrameElement, height: number): number {
  const clamped = clampHeight(height);
  iframe.style.height = `${clamped}px`;
  return clamped;
}

/** Posts a message to the iframe's `contentWindow` with an explicit,
 * never-wildcard `targetOrigin` (threat matrix: outbound targetOrigin). */
export function postToIframe(
  iframe: HTMLIFrameElement,
  message: ToIframeMessage,
  targetOrigin: string,
): void {
  if (targetOrigin === '*') {
    throw new Error('targetOrigin must never be "*"');
  }
  iframe.contentWindow?.postMessage(message, targetOrigin);
}
